import { DateTime } from "luxon";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import { useDispatch } from "../../redux/hooks";
import { updateUserData } from "../../redux/slices/patient";
import { DialogCloseHandler } from "../../types/dialog";
import { Patient } from "../../types/patient";
import { removeUndefined, validateNotEmpty } from "../../utils/global";
import { dateTimeToIsoString, dateToIsoString, isoDateTimeToLuxonUndef } from "../../utils/dateTime";
import BaseFormDialog from "../../components/dialogs/BaseFormDialog";
import { Typography, useTheme } from "@mui/material";

type UserValues = {
  startTimestamp: DateTime | null,
  dueDate: DateTime | null,
}

const DialogModifyUser = ({
  user,
  open,
  onClose,
}: {
  user?: Patient,
  open: boolean,
  onClose?: DialogCloseHandler,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const handleValidate = (values: UserValues) => removeUndefined({
    startTimestamp: validateNotEmpty(values.startTimestamp),
    dueDate:        validateNotEmpty(values.startTimestamp),
  })

  const handleSubmit = (values: UserValues) => {
    if (!user || !values.startTimestamp || !values.dueDate) return;

    // console.log(values)

    dispatch(updateUserData({
      user,
      startTimestamp: dateTimeToIsoString(values.startTimestamp, true),
      dueDate:        dateToIsoString(values.dueDate),
    }))
    .then(() => { onClose?.({}, 'closeClick') })
    .catch(() => {})
  }

  return (
    <BaseFormDialog
      title={`Edit details for user ${user?.id || '???'}`}
      width={320}
      open={open}
      onClose={onClose}
      initialValues={{
        dueDate:        isoDateTimeToLuxonUndef(user?.dueDate)        || null,
        startTimestamp: isoDateTimeToLuxonUndef(user?.startTimestamp) || null,
      } as UserValues}
      validate={handleValidate}
      onSubmit={handleSubmit}
      formSx={{ gap: 1 }}
    >
      {({
        values,
        errors,
        setFieldValue,
      }) => (
        <>
          <DateTimePicker
            label='Start Timestamp'
            value={values.startTimestamp}
            timeSteps={{ hours: 1, minutes: 1 }}
            onChange={val => setFieldValue('startTimestamp', val)}
            slotProps={{
              textField: { required: true, error: !!errors.startTimestamp, helperText: errors.startTimestamp },
              digitalClockSectionItem: { sx: { borderRadius: 100 } },
            }}
          />

          <DatePicker
            label='Due Date'
            value={values.dueDate}
            onChange={val => setFieldValue('dueDate', val)}
            slotProps={{
              textField: { required: true, error: !!errors.dueDate, helperText: errors.dueDate },
            }}
          />

          <Typography variant='caption' sx={{ color: theme.palette.error.main }}>CAUTION: Double-check details before updating!</Typography>
        </>
      )}
    </BaseFormDialog>
  );
}

export default DialogModifyUser;